import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Card,
  CardContent,
  Tabs,
  Tab,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SecurityIcon from '@mui/icons-material/Security';
import ScubaDivingIcon from '@mui/icons-material/ScubaDiving';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GroupIcon from '@mui/icons-material/Group';
import { useAuth } from '../../../contexts/AuthContext.js';
import ClaimAdmin from './ClaimAdmin.js';
import GroupAdmin from './GroupAdmin.js';

// Helper component for Tab Panels
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// User Management Panel Component
const UserManagementPanel = () => {
  const { currentUser } = useAuth();
  const [searchField, setSearchField] = useState('email');
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDivesites, setUserDivesites] = useState([]);
  const [userDives, setUserDives] = useState([]);
  const [userTabValue, setUserTabValue] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteWithContent, setDeleteWithContent] = useState(false);
  const [loadingDivesites, setLoadingDivesites] = useState(false);
  const [loadingDives, setLoadingDives] = useState(false);

  // Fetch initial users list
  useEffect(() => {
    if (currentUser) {
      fetchUsers();
    }
  }, [page, rowsPerPage, currentUser]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const fetchUsers = async () => {
    if (!currentUser) return;
    setLoading(true);
    setError(null);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`/api/admin/users?page=${page + 1}&limit=${rowsPerPage}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data.users || []);
      setTotalUsers(data.totalUsers || 0);
    } catch (err) {
      setError(`Error fetching users: ${err.message}`);
      console.error('Error fetching users:', err);
      setUsers([]);
      setTotalUsers(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!currentUser) return;
    if (!searchValue.trim()) {
      setError('Please enter a search value');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/admin/search-users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          searchField,
          searchValue
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Search failed');
      }

      const data = await response.json();
      setUsers(Array.isArray(data) ? data : []);
      setTotalUsers(Array.isArray(data) ? data.length : 0);
      setPage(0);

      if (data.length === 0) {
        setSuccess('No users found matching your search criteria');
      }
    } catch (err) {
      setError(`Search error: ${err.message}`);
      console.error('Search error:', err);
      setUsers([]);
      setTotalUsers(0);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleUserSelect = async (user) => {
    setSelectedUser(user);
    setUserTabValue(0);
    setUserDivesites([]);
    setUserDives([]);
  };

  const handleUserTabChange = (event, newValue) => {
    setUserTabValue(newValue);

    if (newValue === 1 && selectedUser && (selectedUser.diverId || selectedUser.mongoData?._id || selectedUser.diverRecord?._id)) {
      fetchUserDivesites(selectedUser.diverId || selectedUser.mongoData?._id || selectedUser.diverRecord?._id);
    } else if (newValue === 2 && selectedUser && (selectedUser.diverId || selectedUser.mongoData?._id || selectedUser.diverRecord?._id)) {
      fetchUserDives(selectedUser.diverId || selectedUser.mongoData?._id || selectedUser.diverRecord?._id);
    }
  };

  const fetchUserDivesites = async (diverId) => {
    if (!currentUser || !diverId) return;
    setLoadingDivesites(true);
    setError(null);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`/api/admin/user-divesites/${diverId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to fetch user divesites');
      }

      const data = await response.json();
      setUserDivesites(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(`Error fetching divesites: ${err.message}`);
      console.error('Error fetching divesites:', err);
      setUserDivesites([]);
    } finally {
      setLoadingDivesites(false);
    }
  };

  const fetchUserDives = async (diverId) => {
    if (!currentUser || !diverId) return;
    setLoadingDives(true);
    setError(null);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`/api/admin/user-dives/${diverId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to fetch user dives');
      }

      const data = await response.json();
      setUserDives(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(`Error fetching dives: ${err.message}`);
      console.error('Error fetching dives:', err);
      setUserDives([]);
    } finally {
      setLoadingDives(false);
    }
  };

  const handleDeleteUser = () => {
    setDeleteDialogOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!selectedUser || !currentUser) return;

    setLoading(true);
    setError(null);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/admin/delete-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          uid: selectedUser.uid,
          deleteContent: deleteWithContent
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete user');
      }

      const data = await response.json();
      setSuccess(data.message || 'User deleted successfully');
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      setError(`Error deleting user: ${err.message}`);
      console.error('Error deleting user:', err);
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const cancelDeleteUser = () => {
    setDeleteDialogOpen(false);
    setDeleteWithContent(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  if (!currentUser) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading user data...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          User Management
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="search-field-label">Search By</InputLabel>
              <Select
                labelId="search-field-label"
                value={searchField}
                onChange={(e) => setSearchField(e.target.value)}
                label="Search By"
              >
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="displayName">Display Name</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={7}>
            <TextField
              fullWidth
              label={`Search by ${searchField === 'email' ? 'Email' : 'Display Name'}`}
              variant="outlined"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleSearch}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
              disabled={loading}
            >
              Search
            </Button>
          </Grid>
        </Grid>

        <TableContainer component={Paper} sx={{ maxHeight: 440 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Display Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress size={30} />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow
                    key={user.uid}
                    hover
                    onClick={() => handleUserSelect(user)}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: selectedUser?.uid === user.uid ? 'rgba(0, 0, 0, 0.04)' : 'inherit'
                    }}
                  >
                    <TableCell>
                      {user.displayName || user.mongoData?.displayName || 'No Display Name'}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {user.metadata?.creationTime
                        ? formatDate(user.metadata.creationTime)
                        : user.mongoData?.createdAt
                          ? formatDate(user.mongoData.createdAt)
                          : 'Unknown'
                      }
                    </TableCell>
                    <TableCell>
                      {user.disabled && (
                        <Chip label="Disabled" color="error" size="small" />
                      )}
                      {user.customClaims?.isAdmin && (
                        <Chip
                          icon={<AdminPanelSettingsIcon />}
                          label="Admin"
                          color="primary"
                          size="small"
                          sx={{ ml: user.disabled ? 0.5 : 0 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View User Details">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUserSelect(user);
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalUsers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {selectedUser && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              User Details: {selectedUser.displayName || selectedUser.email || selectedUser.uid}
            </Typography>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteUser}
            >
              Delete User
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Tabs value={userTabValue} onChange={handleUserTabChange} aria-label="user details tabs">
            <Tab label="User Info" />
            <Tab label="Divesites" disabled={!(selectedUser.diverId || selectedUser.mongoData?._id || selectedUser.diverRecord?._id)} />
            <Tab label="Dives" disabled={!(selectedUser.diverId || selectedUser.mongoData?._id || selectedUser.diverRecord?._id)} />
            <Tab label="Manage Claims" />
          </Tabs>

          <Box sx={{ mt: 2 }}>
            {/* User Info Tab */}
            {userTabValue === 0 && (
              <Card>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" gutterBottom>Firebase User Information</Typography>
                      <Box sx={{ pl: 2, mb: 3 }}>
                        <Typography variant="body2">
                          <strong>User ID:</strong> {selectedUser.uid}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Email:</strong> {selectedUser.email || 'Not set'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Display Name:</strong> {selectedUser.displayName || 'Not set'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Email Verified:</strong> {selectedUser.emailVerified ? 'Yes' : 'No'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Account Disabled:</strong> {selectedUser.disabled ? 'Yes' : 'No'}
                        </Typography>
                        {selectedUser.metadata && (
                          <>
                            <Typography variant="body2">
                              <strong>Created:</strong> {formatDate(selectedUser.metadata.creationTime)}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Last Sign In:</strong> {selectedUser.metadata.lastSignInTime ? formatDate(selectedUser.metadata.lastSignInTime) : 'Never'}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" gutterBottom>Database User Information</Typography>
                      <Box sx={{ pl: 2 }}>
                        {selectedUser.diverRecord || selectedUser.mongoData ? (
                          <>
                            <Typography variant="body2">
                              <strong>Database ID:</strong> {selectedUser.diverId || selectedUser.mongoData?._id || selectedUser.diverRecord?._id || 'Not available'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Display Name:</strong> {selectedUser.mongoData?.displayName || selectedUser.diverRecord?.displayName || 'Not set'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Unit Preference:</strong> {selectedUser.mongoData?.unitPreference || selectedUser.diverRecord?.unitPreference || 'Not set'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Time Format:</strong> {selectedUser.mongoData?.timeFormat || selectedUser.diverRecord?.timeFormat || 'Not set'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Created At:</strong> {formatDate(selectedUser.mongoData?.createdAt || selectedUser.diverRecord?.createdAt)}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Updated At:</strong> {formatDate(selectedUser.mongoData?.updatedAt || selectedUser.diverRecord?.updatedAt)}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No database record found for this user. Divesites and Dives tabs will be disabled.
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Divesites Tab */}
            {userTabValue === 1 && (
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    <LocationOnIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    User's Dive Sites
                  </Typography>

                  {loadingDivesites ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : userDivesites.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                      This user has not created any dive sites
                    </Typography>
                  ) : (
                    <TableContainer component={Paper} sx={{ maxHeight: 300, mt: 2 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Location</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell>Published</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {userDivesites.map((site) => (
                            <TableRow key={site._id} hover>
                              <TableCell>{site.uddf?.divesite?.site?.name || 'Unnamed Site'}</TableCell>
                              <TableCell>
                                {site.uddf?.divesite?.site?.geography?.location?.name || 'No Location'}
                              </TableCell>
                              <TableCell>{formatDate(site.metadata?.createdAt)}</TableCell>
                              <TableCell>
                                {site.lifecycle?.published ? (
                                  <Chip label="Published" color="success" size="small" />
                                ) : (
                                  <Chip label="Draft" color="default" size="small" />
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Dives Tab */}
            {userTabValue === 2 && (
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    <ScubaDivingIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    User's Dives
                  </Typography>

                  {loadingDives ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : userDives.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                      This user has not logged any dives
                    </Typography>
                  ) : (
                    <TableContainer component={Paper} sx={{ maxHeight: 300, mt: 2 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Dive Site</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Duration</TableCell>
                            <TableCell>Max Depth</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {userDives.map((dive) => (
                            <TableRow key={dive._id} hover>
                              <TableCell>
                                {dive.divesite?.uddf?.divesite?.site?.name || 'Unknown Site'}
                              </TableCell>
                              <TableCell>{formatDate(dive.diveDate)}</TableCell>
                              <TableCell>
                                {dive.duration ? `${Math.floor(dive.duration / 60)}:${(dive.duration % 60).toString().padStart(2, '0')}` : 'N/A'}
                              </TableCell>
                              <TableCell>
                                {dive.maxDepth ? `${dive.maxDepth}m` : 'N/A'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Claims Management Tab */}
            {userTabValue === 3 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Manage User Claims
                </Typography>
                <ClaimAdmin
                  setError={setError}
                  setSuccess={setSuccess}
                  preselectedUid={selectedUser.uid}
                />
              </Box>
            )}
          </Box>
        </Paper>
      )}

      {/* Delete User Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDeleteUser}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Confirm User Deletion"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this user? This action cannot be undone.
            <Box sx={{ mt: 2 }}>
              <FormControl component="fieldset">
                <FormControlLabel
                  control={
                    <Switch
                      checked={deleteWithContent}
                      onChange={(e) => setDeleteWithContent(e.target.checked)}
                      color="error"
                    />
                  }
                  label="Also delete all user content (dive sites, dives, etc.)"
                />
              </FormControl>
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteUser} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDeleteUser} color="error" autoFocus disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Main Admin Console Component
const AdminConsole = () => {
  const [mainTabValue, setMainTabValue] = useState(0);

  const handleMainTabChange = (event, newValue) => {
    setMainTabValue(newValue);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper 
        elevation={1} 
        sx={{ 
          borderBottom: 1, 
          borderColor: 'divider', 
          mb: 2, 
          position: 'sticky', 
          top: 0, 
          zIndex: 1100 
        }}
      >
        <Tabs 
          value={mainTabValue} 
          onChange={handleMainTabChange} 
          aria-label="admin console tabs"
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab 
            icon={<PersonIcon />} 
            iconPosition="start" 
            label="User Management" 
            id="admin-tab-0" 
            aria-controls="admin-tabpanel-0" 
          />
          <Tab 
            icon={<GroupIcon />} 
            iconPosition="start" 
            label="Group Management" 
            id="admin-tab-1" 
            aria-controls="admin-tabpanel-1" 
          />
        </Tabs>
      </Paper>

      <TabPanel value={mainTabValue} index={0}>
        <UserManagementPanel />
      </TabPanel>
      <TabPanel value={mainTabValue} index={1}>
        <GroupAdmin />
      </TabPanel>
    </Box>
  );
};

export default AdminConsole;
